import Vue from 'vue';
import VueI18n from 'vue-i18n';

import { axios, axios_oauth } from '@/plugins/axios';

import { languages, defaultLocale } from '@/translations';

import i18n_iso_countries from 'i18n-iso-countries';

Vue.use(VueI18n);

const language = window.localStorage.getItem('lang') || defaultLocale;
const messages = Object.assign(languages);

export const i18n = new VueI18n({
	locale: language,
	fallbackLocale: defaultLocale,
	messages
});

// Set messages for default language
// i18n.setLocaleMessage(defaultLocale, fallbackLangMessages);

const loadedLanguages = [defaultLocale]; // Our default language that is preloaded

if (axios !== undefined && axios.interceptors !== undefined) {
	axios.interceptors.request.use((config) => {
		if (config.headers === undefined) {
			config.headers = {};
		}

		config.headers['Accept-Language'] = i18n.locale;

		return config;
	});
}

if (axios_oauth !== undefined && axios_oauth.interceptors !== undefined) {
	axios_oauth.interceptors.request.use((config) => {
		if (config.headers === undefined) {
			config.headers = {};
		}

		config.headers['Accept-Language'] = i18n.locale;

		return config;
	});
}

function setI18nLanguage(lang: string): string {
	i18n.locale = lang;
	window.localStorage.setItem('lang', lang);

	const html = document.querySelector('html');

	if (html) {
		html.setAttribute('lang', lang);
	}

	return lang;
}

/**
 * The function to change language, which loads
 * the translation file if needed with webpack dynamic import
 *
 * @param {string} lang The lang to use
 * @returns {Promise<string>}
 */
export async function loadLanguageAsync(lang: string): Promise<string> {
	const i18n_iso_countries_local_lang = await import('i18n-iso-countries/langs/' + lang + '.json');
	i18n_iso_countries.registerLocale(i18n_iso_countries_local_lang);
	// If the same language
	if (i18n.locale === lang) {
		return Promise.resolve(setI18nLanguage(lang));
	}

	// If the language was already loaded
	if (loadedLanguages.includes(lang)) {
		return Promise.resolve(setI18nLanguage(lang));
	}

	// If the language isn't supported
	if (!Object.hasOwn(languages, lang)) {
		return Promise.reject(lang);
	}

	// If the language hasn't been loaded yet
	const messages = await import(/* webpackChunkName: "translations-[request]" */ `@/translations/${lang}`);
	i18n.setLocaleMessage(lang, messages.default);
	loadedLanguages.push(lang);

	return setI18nLanguage(lang);
}
